import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCloneTaxonomy } from '@/hooks/useCloneTaxonomy';
import { useGetAdminTaxonomiesVersions } from '@/hooks/useGetAdminTaxonomyVersions';

const cloneTaxonomySchema = z.object({
  sourceCustomerId: z.string().min(1, 'Source customer is required'),
  sourceVersion: z.string().min(1, 'Source version is required'),
  destinationCustomerId: z.string().min(1, 'Destination customer is required'),
});

type CloneTaxonomyFormData = z.infer<typeof cloneTaxonomySchema>;

interface CloneTaxonomyFormProps {
  open: boolean;
  onClose: () => void;
}

export function CloneTaxonomyForm({ open, onClose }: CloneTaxonomyFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CloneTaxonomyFormData>({
    resolver: zodResolver(cloneTaxonomySchema),
    defaultValues: {
      sourceCustomerId: '',
      sourceVersion: '',
      destinationCustomerId: '',
    },
  });

  const sourceCustomerId = watch('sourceCustomerId');

  const { versions, versionsLoading } = useGetAdminTaxonomiesVersions(
    sourceCustomerId ? parseInt(sourceCustomerId, 10) : 0,
  );

  const { cloneTaxonomy, isLoading } = useCloneTaxonomy();

  async function onSubmit(data: CloneTaxonomyFormData) {
    try {
      await cloneTaxonomy({
        sourceCustomerId: parseInt(data.sourceCustomerId, 10),
        sourceVersion: data.sourceVersion,
        targetCustomerId: parseInt(data.destinationCustomerId, 10),
      });

      toast.success('Taxonomy cloned successfully!');
      reset();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to clone taxonomy. Please try again.',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Clone Taxonomy</DialogTitle>
          <DialogDescription>
            Select a source taxonomy version and destination customer to clone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-semibold">Source</h3>

            <FormGroup
              error={{ message: errors.sourceCustomerId?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="sourceCustomerId">
                Source Customer
              </RequiredLabel>
              <Controller
                name="sourceCustomerId"
                control={control}
                render={({ field }) => (
                  <CustomerSelect
                    onValueChange={field.onChange}
                    value={field.value}
                    additionalOptions={[{ value: '0', label: 'Default (0)' }]}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.sourceVersion?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="sourceVersion">
                Source Version
              </RequiredLabel>
              <Controller
                name="sourceVersion"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!sourceCustomerId || versionsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select version" />
                    </SelectTrigger>
                    <SelectContent>
                      {versionsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading...
                        </SelectItem>
                      ) : versions.length === 0 ? (
                        <SelectItem value="empty" disabled>
                          No versions found
                        </SelectItem>
                      ) : (
                        versions.map((version) => (
                          <SelectItem key={version} value={version}>
                            {version}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
            </FormGroup>
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-semibold">Destination</h3>

            <FormGroup
              error={{
                message: errors.destinationCustomerId?.message,
                font: 'sm',
              }}
            >
              <RequiredLabel htmlFor="destinationCustomerId">
                Destination Customer
              </RequiredLabel>
              <Controller
                name="destinationCustomerId"
                control={control}
                render={({ field }) => (
                  <CustomerSelect
                    onValueChange={field.onChange}
                    value={field.value}
                    additionalOptions={[{ value: '0', label: 'Default (0)' }]}
                  />
                )}
              />
            </FormGroup>

            <p className="text-sm text-muted-foreground">
              The new version will be calculated automatically by the backend.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Cloning...' : 'Clone Taxonomy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
